// Reports — landing page with the 5 KEEP weekly reports per the
// 2026-05-18 commonsku audit. Operator skipped Sales Target (never
// run) and the 4 NEW-LEVERAGE comparison reports (Rep Comparative,
// Client Comparative, Products Sold, Potential Repeat) — those land
// in PR F+ if needed.
//
// Each card is a Link to the report detail page where the rep picks
// a date range and views aggregated data.
import { Link } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { isMarketingOnly } from "@/lib/roles";
import {
  BarChart3,
  TrendingUp,
  Activity,
  Users,
  Coins,
  GitCompareArrows,
  Megaphone,
  History,
} from "lucide-react";

type IconType = React.ComponentType<{ className?: string }>;

interface ReportCard {
  slug: string;
  title: string;
  icon: IconType;
  description: string;
}

const REPORTS: ReportCard[] = [
  {
    slug: "sales-dashboard",
    title: "Sales Dashboard",
    icon: BarChart3,
    description:
      "Total number and dollar amount of quotes, POs, in-production orders, and invoices generated in the selected window.",
  },
  {
    slug: "sales-history",
    title: "Sales History",
    icon: History,
    description:
      "Every invoice from commonsku's history and Flow: sales, cost, gross profit and margin by year, month, rep and client. Admins only.",
  },
  {
    slug: "sales-pipeline",
    title: "Sales Pipeline",
    icon: TrendingUp,
    description:
      "Pre-sales output: presentations sent, estimates created, and opportunities opened with budget totals.",
  },
  {
    slug: "sales-activity",
    title: "Sales Activity",
    icon: Activity,
    description:
      "Touchpoints by type (calls, meetings, notes, emails) plus creation events across projects, presentations, orders, and invoices.",
  },
  {
    slug: "sales-rep",
    title: "Sales Rep",
    icon: Users,
    description:
      "Per-rep rollup of presentations, estimates, quotes, and invoiced dollars. Sortable by invoiced total.",
  },
  {
    slug: "commissions",
    title: "Commissions",
    icon: Coins,
    description:
      "Your commissions (admin and finance see every rep) — filter by status, whether the client has paid, and whether the vendor bills are in.",
  },
  {
    slug: "sales-comparison",
    title: "Sales Comparison",
    icon: GitCompareArrows,
    description:
      "Revenue, order count, and average order value for the current period vs the prior period — month, quarter, or year.",
  },
  {
    slug: "advertising",
    title: "Advertising",
    icon: Megaphone,
    description:
      "Ad spend, leads, orders, revenue and gross profit by channel and campaign — last or first touch — plus what's been sent to Google and Microsoft. Admin and finance (Marketing sees the same numbers in revenue on Hub ▸ Marketing ▸ Channels).",
  },
];

export function ReportsListPage() {
  // The Marketing role never sees commissions (CEO 2026-10-21) nor Advertising —
  // it is gross profit and payback; "profit and margin aren't a Marketing
  // system" (CEO 2026-10-22). Hub ▸ Marketing ▸ Channels has it in revenue.
  const { user } = useAuth();
  const MARKETING_HIDDEN = ["commissions", "advertising"];
  const shown = isMarketingOnly(user?.roles) ? REPORTS.filter((r) => !MARKETING_HIDDEN.includes(r.slug)) : REPORTS;
  // Sales History is profit and margin for the whole company — admins only (Jake 2026-10-31).
  const listed = user?.roles?.includes("admin") ? shown : shown.filter((r) => r.slug !== "sales-history");
  return (
    <div className="mx-auto w-full">
      <div className="mb-10">
        <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted">
          Reports
        </p>
        <h1 className="mt-2 text-[32px] font-semibold leading-[1.15] tracking-[-1px] text-ink">
          Run a report.
        </h1>
        <p className="mt-2 max-w-2xl text-sm text-brown-400">
          Pick a window, scope to a rep if useful, read the numbers. Most
          reports open on the last 30 days.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        {listed.map((r) => (
          <Link
            key={r.slug}
            to={`/reports/${r.slug}`}
            className="group flex flex-col rounded-lg border border-brown-100 bg-white p-5 transition-colors hover:bg-cream-50"
          >
            <div className="flex items-center gap-3">
              <r.icon className="h-5 w-5 text-brown-400 group-hover:text-brown-500" />
              <h2 className="font-display text-xl font-bold text-brown-500">
                {r.title}
              </h2>
            </div>
            <p className="mt-3 text-sm text-brown-400">{r.description}</p>
          </Link>
        ))}
      </div>
    </div>
  );
}
